import { useCallback, useEffect, useMemo, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

export interface InstallPromptState {
  canInstall: boolean;
  installLabel: string;
  isInstalled: boolean;
  isIos: boolean;
  isIosManualInstall: boolean;
  requiresHomeScreenForPush: boolean;
  promptInstall: () => Promise<void>;
}

const isRunningStandalone = (): boolean => {
  const displayMode = window.matchMedia("(display-mode: standalone)").matches;
  const navigatorWithStandalone = navigator as Navigator & {
    standalone?: boolean;
  };

  return displayMode || navigatorWithStandalone.standalone === true;
};

const isIosBrowser = (): boolean =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const showIosInstallInstructions = (): void => {
  window.alert(
    "To install SellBot AiML on iPhone or iPad, open this page in Safari, tap Share, then choose Add to Home Screen."
  );
};

const useInstallPrompt = (): InstallPromptState => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return isRunningStandalone();
  });

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const handleInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    };

    const displayModeQuery = window.matchMedia("(display-mode: standalone)");
    const handleDisplayModeChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setIsInstalled(true);
        setDeferredPrompt(null);
      }
    };

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt as EventListener
    );
    window.addEventListener("appinstalled", handleInstalled);

    if (displayModeQuery.addEventListener) {
      displayModeQuery.addEventListener("change", handleDisplayModeChange);
    } else {
      displayModeQuery.addListener(handleDisplayModeChange);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt as EventListener
      );
      window.removeEventListener("appinstalled", handleInstalled);

      if (displayModeQuery.removeEventListener) {
        displayModeQuery.removeEventListener("change", handleDisplayModeChange);
      } else {
        displayModeQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, []);

  const isIosManualInstall = useMemo(
    () => !isInstalled && isIosBrowser(),
    [isInstalled]
  );
  const isIos = useMemo(() => isIosBrowser(), []);

  const promptInstall = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;

      if (choice.outcome === "accepted") {
        setIsInstalled(true);
      }

      setDeferredPrompt(null);
      return;
    }

    if (isIosManualInstall) {
      showIosInstallInstructions();
    }
  }, [deferredPrompt, isIosManualInstall]);

  return {
    canInstall: !isInstalled && (!!deferredPrompt || isIosManualInstall),
    installLabel: isIosManualInstall ? "Add to Home Screen" : "Install app",
    isInstalled,
    isIos,
    isIosManualInstall,
    requiresHomeScreenForPush: isIosManualInstall,
    promptInstall,
  };
};

export default useInstallPrompt;
